import React from "react"
import truncateHtml from "truncate-html"
import he from "he"

const fixLocalUrl = (url) => {
  if (url && url.startsWith(`/`)) return process.env.URL + url
  return url
}

const Meta = ({ metaSite, location, data = {} }) => {
  Object.keys(data).forEach((k) => !data[k] && delete data[k])

  const meta = {
    ...metaSite,
    ...data,
  }

  let fullTitle = meta.title
  if (!meta.titleOverridePattern && meta.titlePattern) {
    fullTitle = meta.titlePattern
      .replace(`[PAGE_TITLE]`, meta.title)
      .replace(`[SITE_NAME]`, meta.name)
  }

  if (meta.description) {
    meta.description = truncateHtml(meta.description, {
      length: 240,
      stripTags: true,
    })
  }

  meta.canonical = fixLocalUrl(meta.canonical || location.pathname)

  meta.socialImage = fixLocalUrl(meta.socialImage)

  meta.ogTitle = meta.ogTitle || meta.title
  meta.ogDescription = meta.ogDescription || meta.description
  meta.ogImage = fixLocalUrl(meta.ogImage) || meta.socialImage

  meta.twitterTitle = meta.twitterTitle || meta.title
  meta.twitterDescription = meta.twitterDescription || meta.description
  meta.twitterSite = meta.twitterSite || meta.twitterHandle
  meta.twitterCreator = meta.twitterCreator || meta.twitterSite
  meta.twitterImage = fixLocalUrl(meta.twitterImage) || meta.socialImage

  meta.robotsNoIndex = meta.robotsNoIndex ? `noindex` : `index`
  meta.robotsNoFollow = meta.robotsNoFollow ? `nofollow` : `follow`

  const tagsMeta = [
    // general
    {
      name: `description`,
      content: meta.description,
    },
    {
      name: `robots`,
      content: `${meta.robotsNoIndex}, ${meta.robotsNoFollow}`,
    },
    // fb/og
    {
      property: `fb_app_id`,
      content: meta.fbAppId,
    },
    {
      property: `og:site_name`,
      content: meta.name,
    },
    {
      property: `og:title`,
      content: meta.ogTitle,
    },
    {
      property: `og:description`,
      content: meta.ogDescription,
    },
    {
      property: `og:type`,
      content: meta.ogType,
    },
    {
      property: `og:image`,
      content: meta.ogImage,
    },
    // twitter
    {
      name: `twitter:card`,
      content: `summary`,
    },
    {
      name: `twitter:title`,
      content: meta.twitterTitle,
    },
    {
      name: `twitter:description`,
      content: meta.twitterDescription,
    },
    {
      name: `twitter:site`,
      content: meta.twitterSite,
    },
    {
      name: `twitter:creator`,
      content: meta.twitterCreator,
    },
    {
      name: `twitter:image:src`,
      content: meta.twitterImage,
    },
    // google+, schema.org, other
    {
      itemProp: `name`,
      content: fullTitle,
    },
    {
      itemProp: `description`,
      content: meta.description,
    },
    {
      itemProp: `image`,
      content: meta.socialImage,
    },
    // ie
    {
      name: `application-name`,
      content: meta.name,
    },
    {
      name: `msapplication-tooltip`,
      content: meta.name,
    },
    {
      name: `msapplication-TileImage`,
      content: meta.msTileIcon,
    },
    {
      name: `msapplication-TileColor`,
      content: meta.msTileColor,
    },
    // ios
    {
      name: `apple-touch-icon`,
      content: meta.appleTouchIcon,
    },
    {
      name: `apple-mobile-web-app-title`,
      content: meta.name,
    },
    {
      name: `apple-mobile-web-app-capable`,
      content: meta.appleWebAppCapable,
    },
    {
      name: `apple-mobile-web-app-status-bar-style`,
      content: meta.appleStatusBarStyle,
    },
  ]

  const tagsLink = [
    {
      rel: `canonical`,
      href: meta.canonical,
    },
    {
      rel: `image_src`,
      href: meta.socialImage,
    },
    {
      rel: `icon`,
      href: meta.favIcon,
    },
  ]

  return (
    <>
      <html lang={meta.lang} />

      <title>{he.unescape(fullTitle)}</title>

      {tagsMeta.map((tag, i) => {
        const id = `meta-${Object.entries(tag)
          .slice(0, 1)
          .map(([k, v]) => `${k}-${v}`)}`

        return <meta key={`${id}-${i}`} id={id} {...tag} />
      })}

      {tagsLink.map((tag, i) => {
        const id = `link-${Object.entries(tag)
          .slice(0, 1)
          .map(([k, v]) => `${k}-${v}`)}`

        return <link key={`${id}-${i}`} id={id} {...tag} />
      })}
    </>
  )
}

export default Meta
